import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Inbox } from "lucide-react";
import { BrandMark } from "../components/Emblem";
import useSeo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Retry once after refreshing the access token when it has expired (30-min lifetime)
async function authedRequest(method, url, data) {
  const req = () => axios.request({ method, url, data, withCredentials: true });
  try {
    return await req();
  } catch (err) {
    if (err.response?.status === 401) {
      await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      return await req();
    }
    throw err;
  }
}

const authedGet = (url) => authedRequest("get", url);

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const inputCls =
  "w-full border border-gold/20 bg-surface px-4 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/80 outline-none transition-colors duration-300 focus:border-gold";

const STATUS_META = {
  new: { label: "New", chip: "border-gold bg-gold/15 text-gold-light" },
  contacted: { label: "Contacted", chip: "border-gold/40 text-gold-light" },
  quoted: { label: "Quoted", chip: "border-sky-400/50 text-sky-200" },
  converted: { label: "Converted", chip: "border-emerald-400/50 text-emerald-200" },
  lost: { label: "Lost", chip: "border-foreground/20 text-muted-foreground" },
};
const STATUS_ORDER = ["new", "contacted", "quoted", "converted", "lost"];

function DeliveryChip({ channel, rec, id }) {
  const status = rec?.status || "pending";
  const tone = status === "sent" ? "border-emerald-400/50 text-emerald-200"
    : status === "failed" ? "border-red-500/50 text-red-300"
    : "border-foreground/20 text-muted-foreground";
  const label = status === "sent" ? `${channel} sent` : status === "failed" ? `${channel} failed`
    : status === "not_configured" ? `${channel} off` : `${channel} pending`;
  const title = rec?.provider ? `${rec.provider} · ${rec.reference || ""} · ${rec.attempts || 1} attempt(s)` : "";
  return (
    <span title={title} className={`border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] ${tone}`} data-testid={`admin-delivery-${channel.toLowerCase()}-${id}`}>
      {label}
    </span>
  );
}

function ChannelAudit({ config }) {
  if (!config) return null;
  const Item = ({ ok, label, testId }) => (
    <span className={`border px-3 py-1 text-[9px] font-bold uppercase tracking-[0.18em] ${ok ? "border-emerald-400/50 text-emerald-200" : "border-red-500/40 text-red-300"}`} data-testid={testId}>
      {label}: {ok ? "on" : "off"}
    </span>
  );
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="admin-channel-audit">
      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">Alert channels</span>
      <Item ok={config.gmail_smtp} label="Gmail SMTP" testId="admin-channel-gmail" />
      <Item ok={config.managed_email_fallback} label="Email fallback" testId="admin-channel-managed-email" />
      <Item ok={config.twilio_whatsapp || config.meta_whatsapp} label="WhatsApp API" testId="admin-channel-whatsapp" />
      <span className="text-xs text-muted-foreground" data-testid="admin-channel-recipients">
        → {config.email_recipient || "no email set"}{config.whatsapp_recipient ? ` · +${config.whatsapp_recipient}` : ""}
      </span>
    </div>
  );
}

export default function Admin() {
  useSeo("Admin", "Administrator access — enquiries dashboard.");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [config, setConfig] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState("");

  const loadEnquiries = async () => {
    const { data } = await authedGet(`${API}/enquiries`);
    setEnquiries(data.enquiries);
    authedGet(`${API}/notifications/config`).then((r) => setConfig(r.data)).catch(() => setConfig(null));
  };

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await authedRequest("patch", `${API}/enquiries/${id}/status`, { status });
      setEnquiries((list) => list.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Enquiry marked as ${STATUS_META[status].label}.`);
    } catch {
      toast.error("Could not update the status. Please try again.");
    } finally {
      setBusyId("");
    }
  };

  const resendAlerts = async (id) => {
    setBusyId(id);
    try {
      const { data } = await authedRequest("post", `${API}/enquiries/${id}/notify`);
      setEnquiries((list) => list.map((q) => (q.id === id ? { ...q, notifications: data.notifications } : q)));
      const e = data.notifications?.email?.status, w = data.notifications?.whatsapp?.status;
      toast[e === "sent" ? "success" : "error"](`Alerts re-sent — email: ${e || "n/a"}, WhatsApp: ${w || "n/a"}.`);
    } catch {
      toast.error("Could not resend the alerts. Please try again.");
    } finally {
      setBusyId("");
    }
  };

  const removeEnquiry = async (id) => {
    setBusyId(id);
    try {
      await authedRequest("delete", `${API}/enquiries/${id}`);
      setEnquiries((list) => list.filter((q) => q.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete the enquiry. Please try again.");
    } finally {
      setBusyId("");
      setConfirmDeleteId("");
    }
  };

  useEffect(() => {
    authedGet(`${API}/auth/me`)
      .then(async ({ data }) => {
        setUser(data);
        await loadEnquiries();
      })
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      await loadEnquiries();
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setEnquiries([]);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!user) {
    return (
      <section className="flex min-h-screen items-center justify-center px-6 pt-20" data-testid="admin-login-section">
        <div className="w-full max-w-md border border-gold/20 bg-surface p-10">
          <BrandMark className="h-14 w-14" />
          <h1 className="mt-6 font-heading text-2xl text-foreground">Administrator Access</h1>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to view transportation enquiries.</p>
          <form onSubmit={login} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">Email</label>
              <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">Password</label>
              <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" />
            </div>
            {error && <p className="border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-red-300" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-solid w-full" data-testid="admin-login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10" data-testid="admin-dashboard">
      <div className="flex flex-col justify-between gap-6 border-b border-gold/15 pb-8 sm:flex-row sm:items-center">
        <div>
          <p className="overline-gold">Administration</p>
          <h1 className="mt-3 font-heading text-3xl text-foreground">Transportation Enquiries</h1>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="admin-enquiries-count">
            {enquiries.length} {enquiries.length === 1 ? "enquiry" : "enquiries"} received
          </p>
          <ChannelAudit config={config} />
        </div>
        <button onClick={logout} className="btn-gold !px-6 !py-3" data-testid="admin-logout-button">
          <LogOut className="h-4 w-4" /> Sign Out
        </button>
      </div>

      {enquiries.length === 0 ? (
        <div className="flex flex-col items-center gap-4 py-24 text-center" data-testid="admin-empty-state">
          <Inbox className="h-10 w-10 text-gold/70" />
          <p className="text-sm text-muted-foreground">No enquiries yet. New submissions will appear here.</p>
        </div>
      ) : (
        <div className="mt-10 space-y-px border border-gold/15 bg-gold/15" data-testid="admin-enquiries-list">
          {enquiries.map((q) => {
            const meta = STATUS_META[q.status] || STATUS_META.new;
            return (
            <article key={q.id} className="grid gap-4 bg-ink p-6 lg:grid-cols-12" data-testid={`admin-enquiry-${q.id}`}>
              <div className="lg:col-span-3">
                <p className="font-semibold text-foreground">{q.full_name}</p>
                <p className="mt-1 text-xs text-muted-foreground">{q.company_name || "—"}</p>
                <p className="mt-1 text-xs text-foreground">{q.phone}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{q.email}</p>
              </div>
              <div className="lg:col-span-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">Route</p>
                <p className="mt-1 text-sm text-foreground/90">{q.pickup_location} → {q.delivery_location}</p>
                <p className="mt-1 text-xs text-muted-foreground">{q.cargo_type}{q.approximate_load ? ` · ${q.approximate_load}` : ""}</p>
              </div>
              <div className="lg:col-span-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">Requirement</p>
                <p className="mt-1 text-sm text-foreground/90">{q.requirement}</p>
                {q.additional_info && <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{q.additional_info}</p>}
              </div>
              <div className="lg:col-span-2 lg:text-right">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">Received</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {q.created_at ? new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                </p>
                {q.required_date && <p className="mt-1 text-xs text-foreground/80">Needed: {q.required_date}</p>}
                <div className="mt-3 flex flex-wrap gap-1.5 lg:justify-end" data-testid={`admin-delivery-${q.id}`}>
                  <DeliveryChip channel="Email" rec={q.notifications?.email} id={q.id} />
                  <DeliveryChip channel="WhatsApp" rec={q.notifications?.whatsapp} id={q.id} />
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 lg:justify-end" data-testid={`admin-actions-${q.id}`}>
                  <span className={`border px-3 py-1 text-[9px] font-bold uppercase tracking-[0.18em] ${meta.chip}`} data-testid={`admin-status-${q.id}`}>
                    {meta.label}
                  </span>
                  <select
                    value={STATUS_META[q.status] ? q.status : "new"}
                    onChange={(e) => updateStatus(q.id, e.target.value)}
                    disabled={busyId === q.id}
                    className="border border-gold/30 bg-surface px-2 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-foreground/80 outline-none transition-colors hover:border-gold focus:border-gold disabled:opacity-50"
                    data-testid={`admin-status-select-${q.id}`}
                    aria-label="Update enquiry status"
                  >
                    {STATUS_ORDER.map((s) => (
                      <option key={s} value={s}>{STATUS_META[s].label}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => resendAlerts(q.id)}
                    disabled={busyId === q.id}
                    className="border border-gold/30 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-foreground/70 transition-colors hover:border-gold hover:text-gold-light disabled:opacity-50"
                    data-testid={`admin-resend-${q.id}`}
                  >
                    {busyId === q.id ? "Working…" : "Resend alerts"}
                  </button>
                  {confirmDeleteId === q.id ? (
                    <>
                      <button
                        onClick={() => removeEnquiry(q.id)}
                        disabled={busyId === q.id}
                        className="border border-red-500/50 bg-red-500/10 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-red-300 transition-colors hover:bg-red-500/20 disabled:opacity-50"
                        data-testid={`admin-confirm-delete-${q.id}`}
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId("")}
                        className="border border-foreground/20 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
                        data-testid={`admin-cancel-delete-${q.id}`}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(q.id)}
                      className="border border-foreground/20 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:border-red-500/50 hover:text-red-300"
                      data-testid={`admin-delete-${q.id}`}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
